<template>
  <transition name="ai-panel" @after-leave="onPanelLeave">
    <div
      v-show="aiPanelOpen && !fullScreen"
      class="site-ai-panel-host"
      :class="{ 'is-floating': aiPanelFloating }"
      :style="aiPanelFloating ? { left: aiPanelPos.x + 'px', top: aiPanelPos.y + 'px', width: aiPanelSize.w + 'px', height: aiPanelSize.h + 'px' } : null"
    >
      <!-- AI 会话窗口：非浮动时层叠覆盖右侧配置面板(380px)，浮动时拆成可拖拽浮窗 -->
      <div class="site-ai-panel">
        <div v-show="aiPanelFloating" class="site-ai-panel__titlebar" @pointerdown="startAiPanelDrag">
          <span>Kop AI</span>
        </div>
        <iframe class="site-ai-panel__iframe" :src="siteAiUrl" frameborder="0" />
        <div v-show="aiPanelFloating" class="site-ai-panel__resize" @pointerdown="startAiPanelResize">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
            <path d="M4 12 L12 4" />
            <path d="M8 12 L12 8" />
          </svg>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { debounce } from 'lodash-es'
import { Message } from 'element-ui'
import { on, off } from '~/utils/dom'
import { TokenName } from '~/config/cookieKeys'
import { PostMessageEvent } from '~/enums/postMessageEvent'
import { postMessageToAiIframe } from '~/utils/event'

// AI 浮动窗口尺寸约束
const AI_PANEL_MIN_W = 320
const AI_PANEL_MAX_W = 640 // resize 最大宽度
const AI_PANEL_MIN_H = 400
const AI_PANEL_DEFAULT_W = 400
const AI_PANEL_DEFAULT_H_MARGIN = 120 // 默认高度 = min(视口高 - 120, 900)
const AI_PANEL_DEFAULT_H_CAP = 900
const AI_PANEL_MAX_H_MARGIN = 88 // resize 最大高度 = 视口高 - 88
const AI_PANEL_GUTTER = 16

export default {
  name: 'SiteAiPanel',
  props: {
    siteAiUrl: { type: String, required: true },
    // SiteCore 实例，提供 getConfig()/lang/site 等
    model: { type: Object, default: null },
    selectWidget: { type: Object, default: null },
    siteInfo: { type: Object, default: () => ({}) },
    baseInfo: { type: Object, default: () => ({}) },
    pageList: { type: Array, default: () => [] },
    fullScreen: { type: Boolean, default: false },
  },
  data() {
    return {
      siteAiPanelReady: false,
      aiPanelOpen: false,
      aiPanelFloating: false,
      aiPanelPos: { x: 0, y: 0 },
      aiPanelSize: { w: AI_PANEL_DEFAULT_W, h: 0 },
      _aiDrag: null,
      _aiResize: null,
    }
  },
  computed: {
    siteAiOrigin() {
      try {
        return new URL(this.siteAiUrl).origin
      } catch (e) {
        return ''
      }
    },
    aiSiteValue() {
      const site = this.model?.site
      if (!site) return ''
      return JSON.stringify(site, (key, val) => {
        if (key.includes('__')) return undefined
        return val
      })
    },
    aiSelectWidgetValue() {
      if (!this.selectWidget) return ''
      return JSON.stringify(this.selectWidget, (key, val) => {
        if (key.includes('__')) return undefined
        return val
      })
    },
  },
  watch: {
    aiSiteValue() {
      if (this.siteAiPanelReady) {
        this.debouncedAiSync()
      }
    },
    aiSelectWidgetValue() {
      if (this.siteAiPanelReady) {
        this.debouncedAiSync()
      }
    },
  },
  created() {
    this.debouncedAiSync = debounce(() => {
      this.sendAiSiteData(PostMessageEvent.AI_SITE_DATA_SYNC)
    }, 400)
  },
  mounted() {
    on(window, 'message', this.receiveMessage)
  },
  beforeDestroy() {
    off(window, 'message', this.receiveMessage)
    this.onAiPanelDragEnd()
    this.onAiPanelResizeEnd()
    this.debouncedAiSync && this.debouncedAiSync.cancel()
  },
  methods: {
    sendAiSiteData(eventType) {
      const iframe = this.$el?.querySelector('.site-ai-panel__iframe')
      if (!iframe?.contentWindow || !this.model?.getConfig) return

      const rawPayload = {
        siteConfig: this.model.getConfig(),
        selectWidget: this.selectWidget
          ? JSON.parse(
              JSON.stringify(this.selectWidget, (key, val) => {
                if (key.includes('__')) return undefined
                return val
              })
            )
          : null,
        siteInfo: {
          siteId: this.baseInfo.siteId,
          pageId: this.baseInfo.pageId,
          baseDomain: this.baseInfo.baseDomain,
          merchantId: this.siteInfo.merchant_id,
          domain: this.baseInfo.domain,
        },
        pageList: this.pageList.map((p) => ({ id: p.id, name: p.name, link: p.link })),
        lang: this.model.lang || '',
        projectId: this.siteInfo.project_id,
        // AI 链路：与 kop-shop 同源，读商户 token 下发给 AI 面板，供其调 kopglobal 道具/商品只读接口
        token: this.$cookies?.get(TokenName) || '',
        // AI 链路：当前部署环境（test/pre/release/dev），供后端选择对应的 kopglobal 接口域名
        env: process.env.RUN_ENV || '',
        timestamp: Date.now(),
      }

      // 通过 JSON 序列化确保 payload 为纯对象，避免 Vue 响应式对象导致 postMessage 克隆失败
      const payload = JSON.parse(JSON.stringify(rawPayload))

      console.log(`[SiteAiSync] ${eventType}`, payload)

      postMessageToAiIframe(iframe, { type: eventType, payload }, this.siteAiOrigin)
    },
    receiveMessage(event) {
      const origin = event.origin || event.originalEvent.origin
      if (!origin) return

      // 只处理来自 AI iframe 子窗口的消息；父页消息由宿主页面(index.vue)处理
      if (origin === this.siteAiOrigin) {
        this.dealAiMessageData(event.data)
      }
    },
    dealAiMessageData(data) {
      if (!data || !data.type) return

      try {
        switch (data.type) {
          case PostMessageEvent.AI_PANEL_READY:
            this.siteAiPanelReady = true
            this.sendAiSiteData(PostMessageEvent.AI_SITE_DATA_FULL)
            break
          case PostMessageEvent.AI_GET_CURRENT_DATA:
            this.sendAiSiteData(PostMessageEvent.AI_SITE_DATA_FULL)
            break
          case PostMessageEvent.AI_MODULE_APPLY:
            this.$emit('apply-modules', data)
            break
          case PostMessageEvent.AI_SELECT_WIDGET:
            if (data.payload?.widgetId) {
              this.$emit('select-widget', data.payload.widgetId)
            }
            break
          case PostMessageEvent.AI_CONFIG_UPDATE:
            this.$emit('apply-config-update', data)
            break
          case PostMessageEvent.AI_OPEN_ACTIVITY:
            this.$emit('open-activity', data)
            break
          case PostMessageEvent.AI_SET_FLOAT:
            this.setAiPanelFloating(!!data.payload?.floating)
            break
          case PostMessageEvent.AI_CLOSE_PANEL:
            this.closePanel()
            break
        }
      } catch (e) {
        console.error(`[SiteAi] 处理 AI 消息失败 (${data.type}):`, e, data)
        Message.error('AI 配置应用出错，详见控制台')
      }
    },
    sendAiFloatState(floating) {
      const iframe = this.$el?.querySelector('.site-ai-panel__iframe')
      if (!iframe?.contentWindow) return
      postMessageToAiIframe(
        iframe,
        {
          type: PostMessageEvent.AI_FLOAT_STATE,
          payload: { floating: floating === undefined ? this.aiPanelFloating : floating },
        },
        this.siteAiOrigin
      )
    },
    openPanel() {
      this.aiPanelOpen = true
      // 打开时始终以非浮动（层叠覆盖）展示，复位浮动状态并同步给子项目
      this.aiPanelFloating = false
      this.$emit('open-change', true)
      this.$emit('floating-change', false)
      this.sendAiFloatState(false)
    },
    closePanel() {
      this.aiPanelOpen = false
      this.$emit('open-change', false)
      // 立即同步 floating=false 给子项目（避免下次浮动切换点两下）；布局复位延迟到 @after-leave（避免瞬移）
      this.sendAiFloatState(false)
    },
    onPanelLeave() {
      // 淡出动画结束后复位浮动状态（兜底），避免浮窗瞬移回右侧面板
      if (this.aiPanelFloating) {
        this.setAiPanelFloating(false)
      }
    },
    togglePanel() {
      if (this.aiPanelOpen) {
        this.closePanel()
      } else {
        this.openPanel()
      }
    },
    setAiPanelFloating(floating) {
      this.aiPanelFloating = floating
      this.$emit('floating-change', floating)
      this.sendAiFloatState()
      if (!floating) return
      // 首次浮动：尺寸取默认值（高度贴近视口，比 620px 更高），落到右上角
      if (this.aiPanelSize.h === 0) {
        const h = Math.max(AI_PANEL_MIN_H, Math.min(window.innerHeight - AI_PANEL_DEFAULT_H_MARGIN, AI_PANEL_DEFAULT_H_CAP))
        this.aiPanelSize = { w: AI_PANEL_DEFAULT_W, h }
      }
      if (this.aiPanelPos.x === 0 && this.aiPanelPos.y === 0) {
        this.aiPanelPos = {
          x: Math.max(0, window.innerWidth - this.aiPanelSize.w - AI_PANEL_GUTTER),
          y: 64,
        }
      }
    },
    clampAiPanelPos() {
      // 保证浮窗整体始终在视口内：四个方向都不能被拽出屏幕
      const { w, h } = this.aiPanelSize
      const x = Math.max(0, Math.min(this.aiPanelPos.x, window.innerWidth - w))
      const y = Math.max(0, Math.min(this.aiPanelPos.y, window.innerHeight - h))
      this.aiPanelPos = { x, y }
    },
    capturePointer(e) {
      // 捕获指针：即使鼠标快速移过 iframe / 其他区域，也能持续收到 move/up
      const el = e.currentTarget
      if (el && typeof el.setPointerCapture === 'function') {
        try { el.setPointerCapture(e.pointerId) } catch { /* 忽略不支持的情况 */ }
      }
    },
    startAiPanelDrag(e) {
      if (!this.aiPanelFloating || e.button !== 0) return
      e.preventDefault()
      const rect = this.$el?.querySelector('.site-ai-panel')?.getBoundingClientRect()
      if (!rect) return
      const el = e.currentTarget
      this.capturePointer(e)
      this._aiDrag = { el, dx: e.clientX - rect.left, dy: e.clientY - rect.top }
      on(el, 'pointermove', this.onAiPanelDragMove)
      on(el, 'pointerup', this.onAiPanelDragEnd)
      on(el, 'pointercancel', this.onAiPanelDragEnd)
    },
    onAiPanelDragMove(e) {
      const d = this._aiDrag
      if (!d) return
      this.aiPanelPos = { x: e.clientX - d.dx, y: e.clientY - d.dy }
      this.clampAiPanelPos()
    },
    onAiPanelDragEnd() {
      const d = this._aiDrag
      this._aiDrag = null
      if (!d) return
      off(d.el, 'pointermove', this.onAiPanelDragMove)
      off(d.el, 'pointerup', this.onAiPanelDragEnd)
      off(d.el, 'pointercancel', this.onAiPanelDragEnd)
    },
    startAiPanelResize(e) {
      if (!this.aiPanelFloating || e.button !== 0) return
      e.preventDefault()
      const el = e.currentTarget
      this.capturePointer(e)
      this._aiResize = { el, x: e.clientX, y: e.clientY, w: this.aiPanelSize.w, h: this.aiPanelSize.h }
      on(el, 'pointermove', this.onAiPanelResizeMove)
      on(el, 'pointerup', this.onAiPanelResizeEnd)
      on(el, 'pointercancel', this.onAiPanelResizeEnd)
    },
    onAiPanelResizeMove(e) {
      const s = this._aiResize
      if (!s) return
      const maxH = window.innerHeight - AI_PANEL_MAX_H_MARGIN
      let w = s.w + (e.clientX - s.x)
      let h = s.h + (e.clientY - s.y)
      w = Math.max(AI_PANEL_MIN_W, Math.min(w, AI_PANEL_MAX_W))
      h = Math.max(AI_PANEL_MIN_H, Math.min(h, maxH))
      this.aiPanelSize = { w, h }
      this.clampAiPanelPos()
    },
    onAiPanelResizeEnd() {
      const s = this._aiResize
      this._aiResize = null
      if (!s) return
      off(s.el, 'pointermove', this.onAiPanelResizeMove)
      off(s.el, 'pointerup', this.onAiPanelResizeEnd)
      off(s.el, 'pointercancel', this.onAiPanelResizeEnd)
    },
  },
}
</script>

<style lang="less">
.site-ai-panel-host {
  position: absolute;
  top: 0;
  right: 0;
  width: 380px;
  height: calc(100vh - var(--header-height));
  z-index: 40;
  box-sizing: border-box;
  background-color: var(--widget-menu-bg);
  border-left: 1px solid rgba(255, 255, 255, 0.06);

  &.is-floating {
    position: fixed;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
    z-index: 9999; // 浮窗置于最顶层，盖过编辑器面板与弹窗
  }

  .site-ai-panel {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: var(--widget-menu-bg);

    &__titlebar {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      gap: 6px;
      height: 32px;
      padding: 0 10px;
      font-size: 12px;
      color: var(--text-light-color);
      background: rgba(255, 255, 255, 0.05);
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
      cursor: move;
      user-select: none;
      touch-action: none;
    }

    &__resize {
      position: absolute;
      right: 0;
      bottom: 0;
      width: 18px;
      height: 18px;
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
      color: var(--text-color-secondary);
      cursor: nwse-resize;
      z-index: 2;
      user-select: none;
      touch-action: none;

      svg {
        width: 12px;
        height: 12px;
      }
    }

    &__iframe {
      flex: 1;
      width: 100%;
      border: 0;
    }
  }
}

// AI 面板打开/关闭的过渡动画：从右往左展开/收起，与宽度过渡节奏一致
.ai-panel-enter-active,
.ai-panel-leave-active {
  transition: clip-path 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.28s ease;
}
.ai-panel-enter {
  clip-path: inset(0 0 0 100%);
  opacity: 0;
}
.ai-panel-leave-to {
  clip-path: inset(0 0 0 100%);
  opacity: 0;
}
</style>
